import http from "@/http/http"
import type { ResultVO } from "@/api"
import { downloadFile } from "@/util/util"

export interface SandtableTypeItemVO {
  // 沙盘类型标签
  label: string
  // 沙盘类型
  value: string
  // 启用状态 1 - 启用；0 - 未启用
  status: string
  // 是否有权限
  unlocked: boolean
}

// 获取沙盘类型
export async function getSandtableTypes(): Promise<SandtableTypeItemVO[]> {
  try {
    const result = await http.get<SandtableTypeItemVO[]>(`/business/sandTable/type`)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface CreateSandtableArgs {
  // 品牌id
  originBrandId: string
  // 品牌name
  originBrandName: string
  // 沙盘分类（SandtableCategory）
  sandTableCategory: string
  // 点位文件路径
  poiFilePath?: string
  // 沙盘名称
  sandTableName?: string
  // 沙盘类型 （SandtableType）
  sandTableType?: string
  // 沙盘ID（周边生态时使用）
  sandTableId?: string
}

// 创建沙盘
export async function createSandtable(params: CreateSandtableArgs): Promise<ResultVO> {
  try {
    const result = await http.put<ResultVO>(`/business/sandTable`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface UploadPoiResult {
  // 点位文件路径
  poiFilePath: string
  // 总条数
  total: number
  // 成功条数
  success: number
  // 失败条数
  failure: number
}

// 上传点位
export async function uploadSandtablePoi(params: any): Promise<UploadPoiResult> {
  try {
    const result = await http.put<UploadPoiResult>(`/business/sandTable/uploadPoi`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface GetSandtableArgs {
  // 沙盘名称 支持模糊匹配
  sandTableName?: string
  // 沙盘类型（SandtableType）
  sandTableType?: string
  // 沙盘分类（SandtableCategory）
  sandTableCategory?: string
  // 每页条数
  pageSize?: number
  // 页数
  pageNum?: number
}

export interface SandtableItemVO {
  // 沙盘ID
  sandTableId: string
  // 沙盘名称
  sandTableName: string
  // 沙盘类型（SandtableType）
  sandTableType: string
  // 沙盘分类（SandtableCategory）
  sandTableCategory: string
  // 品牌id
  brandId: string
  // 品牌name
  brandName: string
  // 沙盘所属人
  owner: string
  // 删除标识 0 - 正常；1 - 已删除
  delFlag: string
  // 创建时间
  createTime: string
  // 权限是否过期 1 - 有权限；0 - 无权限
  permission: string
}

interface GetSandtableResult {
  rows: SandtableItemVO[]
  total: number
}

// 获取当前用户的沙盘列表
export async function getSandtableList(args: GetSandtableArgs): Promise<GetSandtableResult> {
  try {
    const { pageNum, pageSize, ...others } = args
    const param = pageNum && pageSize ? `?pageNum=${pageNum}&pageSize=${pageSize}` : ""
    const result = await http.post<GetSandtableResult>(`/business/sandTable/list${param}`, others)
    return result as any
  } catch (error) {
    throw error
  }
}

interface UpdateCopySandtableArgs {
  sandTableId: string
  sandTableName: string
}

export type UpdateSandtableArgs = UpdateCopySandtableArgs

// 修改沙盘
export async function updateSandtable(args: UpdateSandtableArgs): Promise<boolean> {
  try {
    const result = await http.patch<boolean>(`/business/sandTable`, args)
    return result?.data
  } catch (error) {
    throw error
  }
}

export type CopySandtableArgs = UpdateCopySandtableArgs

// 复制沙盘
export async function copySandtable(args: CopySandtableArgs): Promise<boolean> {
  try {
    const result = await http.put<boolean>(`/business/sandTable/copy`, args)
    return result?.data
  } catch (error) {
    throw error
  }
}

// 删除沙盘
export async function deleteSandtable(sandTableId: string): Promise<boolean> {
  try {
    const result = await http.delete<boolean>(`/business/sandTable/${sandTableId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}

export type SandtableDetailVO = SandtableItemVO & {
  // 沙盘创建者
  createBy: string
  // 沙盘修改者
  updateBy: string
  // 沙盘修改时间
  updateTime: string
  // 创建沙盘时选择的品牌
  originBrandId: string
  // 用户切换后的品牌
  brandId: string
}

// 获取沙盘详情
export async function getSandtableDetail(sandTableId: string): Promise<SandtableDetailVO> {
  try {
    const result = await http.get<SandtableDetailVO>(`/business/sandTable/${sandTableId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}

// 点位导入模板下载
export async function getPoiTemplate(): Promise<any> {
  try {
    const result: any = await http.get<any>(`/business/sandTable/poiTemplate`, {
      responseType: "blob",
    })
    downloadFile(result, `自定义沙盘点位模板.xlsx`)
    return result.status
  } catch (error) {
    throw error
  }
}
